/* One navigation controller. Existing Shopify links and mega-menu section content remain authoritative. */
(() => {
  if (customElements.get('tgex-navigation')) return;
  class TGEXNavigation extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      this.desktop = window.matchMedia('(min-width: 990px)');
      this.hover = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.mobileButton = this.querySelector('.tgex-nav__mobile-toggle');
      this.menu = this.querySelector('.tgex-nav__menu');
      this.items = [...this.querySelectorAll('.tgex-nav__item')];
      this.mounted = [];
      this.timers = new Map();
      this.listen(this.mobileButton, 'click', () => this.setMobile(!this.classList.contains('is-mobile-open')));
      this.items.forEach(item => {
        const button = item.querySelector('.tgex-nav__toggle');
        const link = item.querySelector('.tgex-nav__link');
        this.listen(button, 'click', () => this.toggle(item));
        this.listen(link, 'click', event => {
          const href = link.getAttribute('href');
          if (!button.hidden && (!href || ['#','/','http://','https://'].includes(href))) { event.preventDefault(); this.toggle(item); }
        });
        this.listen(item, 'pointerenter', event => {
          if (this.desktop.matches && this.hover.matches && event.pointerType !== 'touch' && !button.hidden) this.open(item);
        });
        this.listen(item, 'pointerleave', () => {
          if (!this.desktop.matches || !this.hover.matches) return;
          clearTimeout(this.timers.get(item));
          this.timers.set(item, setTimeout(() => {
            if (!item.matches(':hover') && !item.contains(document.activeElement)) this.close(item);
          }, 260));
        });
        this.listen(item, 'focusout', () => setTimeout(() => {
          if (this.isConnected && this.desktop.matches && !item.contains(document.activeElement) && !item.matches(':hover')) this.close(item);
        }, 0));
        this.listen(item, 'keydown', event => {
          if (event.key === 'ArrowDown' && (event.target === button || event.target === link) && !button.hidden) {
            event.preventDefault(); this.open(item); item.querySelector('.tgex-nav__panel a, .tgex-nav__panel button')?.focus();
          }
          if (event.key === 'Escape' && item.classList.contains('is-open')) {
            event.preventDefault(); event.stopPropagation(); this.close(item); button.focus();
          }
        });
      });
      this.listen(document, 'pointerdown', event => { if (!this.contains(event.target)) this.closeAll(); });
      this.listen(this, 'keydown', event => {
        if (event.key === 'Escape' && this.classList.contains('is-mobile-open')) { this.closeAll(); this.mobileButton.focus(); }
        if (event.key === 'Tab' && this.classList.contains('is-mobile-open')) {
          const controls = [this.mobileButton, ...this.menu.querySelectorAll('a[href],button:not([hidden])')].filter(el => el.getClientRects().length);
          const first = controls[0], last = controls.at(-1);
          if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
        }
      });
      this.listen(this.desktop, 'change', () => { this.closeAll(); this.measure(); });
      this.listen(window, 'resize', () => this.measure());
      this.listen(window, 'scroll', () => this.measure(), {passive:true});
      this.listen(document, 'shopify:section:load', () => this.hydrate());
      this.listen(document, 'shopify:section:unload', event => {
        this.mounted = this.mounted.filter(record => {
          if (event.target.contains(record.marker)) {
            record.panel.remove(); record.fallback?.removeAttribute('hidden');
            record.button.hidden = !record.fallback;
            this.close(record.item); return false;
          }
          return true;
        });
      });
      this.listen(document, 'shopify:block:select', event => {
        const item = event.target.closest('.tgex-nav__item');
        if (item) { if (!this.desktop.matches) this.setMobile(true); this.open(item); }
      });
      this.listen(this.querySelector('.header__search summary'), 'click', () => this.closeAll());
      this.hydrate();
      this.measure();
    }
    listen(target, event, handler, options = {}) { target?.addEventListener(event, handler, {...options, signal:this.abort.signal}); }
    hydrate() {
      document.querySelectorAll('.mega-menu-section .mega-menu[data-parent-link]').forEach(mega => {
        const handle = mega.dataset.parentLink;
        const item = this.items.find(candidate => candidate.dataset.tgexHandle === handle);
        if (!item || !handle || mega.closest('tgex-navigation')) return;
        const panel = mega.closest('.mega-menu__section');
        if (!panel) return;
        const target = item.querySelector('.tgex-nav__panel');
        const button = item.querySelector('.tgex-nav__toggle');
        const fallback = target.querySelector('.tgex-nav__fallback');
        const old = this.mounted.find(record => record.item === item);
        if (old) { old.panel.remove(); old.marker.remove(); this.mounted = this.mounted.filter(record => record !== old); }
        const marker = document.createComment('TGEX menu section position');
        panel.before(marker);
        target.append(panel);
        fallback?.setAttribute('hidden','');
        button.hidden = false;
        item.classList.add('has-mega-panel');
        this.mounted.push({marker,panel,item,button,fallback});
      });
    }
    measure() {
      const bottom = Math.max(0, this.querySelector('.tgex-nav__bar').getBoundingClientRect().bottom);
      this.style.setProperty('--tgex-panel-room', `${Math.max(160, window.innerHeight-bottom-12)}px`);
    }
    toggle(item) { item.classList.contains('is-open') ? this.close(item) : this.open(item); }
    open(item) {
      clearTimeout(this.timers.get(item));
      this.items.forEach(other => { if (other !== item) this.close(other); });
      item.classList.add('is-open');
      item.querySelector('.tgex-nav__toggle').setAttribute('aria-expanded','true');
      item.querySelector('.tgex-nav__panel').hidden = false;
      this.measure();
      this.stickyState();
    }
    close(item) {
      clearTimeout(this.timers.get(item));
      item.classList.remove('is-open');
      item.querySelector('.tgex-nav__toggle').setAttribute('aria-expanded','false');
      item.querySelector('.tgex-nav__panel').hidden = true;
      this.stickyState();
    }
    setMobile(open) {
      this.classList.toggle('is-mobile-open', open);
      this.mobileButton.setAttribute('aria-expanded', String(open));
      this.mobileButton.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
      document.body.classList.toggle('tgex-menu-open', open && !this.desktop.matches);
      if (!open) this.items.forEach(item => this.close(item));
      this.measure();
      this.stickyState();
    }
    stickyState() {
      const header = this.closest('sticky-header');
      if (header) header.preventHide = this.classList.contains('is-mobile-open') || this.items.some(item => item.classList.contains('is-open'));
    }
    closeAll() { this.setMobile(false); }
    disconnectedCallback() {
      this.abort?.abort();
      this.timers?.forEach(timer => clearTimeout(timer));
      document.body.classList.remove('tgex-menu-open');
      this.mounted?.forEach(({marker,panel}) => { if (marker.isConnected) marker.replaceWith(panel); });
    }
  }
  customElements.define('tgex-navigation', TGEXNavigation);
})();
